import { ACTIVE_WORKSPACE_COOKIE, clearAllCookies, clearCookie, getCookie } from './common';
import { clearQueryCache } from './queryClient';

let trigger = false;
const debounceInterval = 1000;
let debounceTimeout;

/**
 * Scopes every request to whichever workspace the user last switched to, so
 * switching workspaces (ProfileMenu) actually changes which workspace's data
 * loads — not just which nav items show. Omitted when there is none yet (first
 * load before workspaces have resolved), so the backend falls back to the
 * workspace baked into the JWT, same as before this existed.
 */
export function workspaceHeader() {
  const workspaceId = getCookie(ACTIVE_WORKSPACE_COOKIE);
  return workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
}

function debounceFetch(url, requestOptions) {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  return new Promise(resolve => {
    debounceTimeout = setTimeout(() => {
      fetch(url, requestOptions).then(res => {
        resolve(handleResponse(res));
      });
    }, debounceInterval);
  });
}

// Only auth knows whether the session is over. A 401 from any other service
// (a misconfigured one, say) fails that request instead of signing the user out;
// a really expired session still ends on the next user-details call.
const AUTH_API_URL = new URL(process.env.REACT_APP_AUTH_API_URL || '/', window.location.origin).href;

function isSessionRejected(response) {
  return response.status === 401 && response.url.startsWith(AUTH_API_URL);
}

function handleResponse(response) {
  if (isSessionRejected(response) && !trigger && getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE)) {
    trigger = true;
    clearAllCookies();
    clearCookie(process.env.REACT_APP_PSP_ALLOWED_PAGES_COOKIE);
    clearQueryCache();
    window.location.reload();
  }
  return response;
}

function get(url, debounce = false, customHeaders = {}) {
  const headers = {
    'X-path': window.location.pathname,
    'Content-Type': 'application/json',
    authorization: `Bearer ${getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE)}`,
    ...workspaceHeader(),
    ...customHeaders,
  };
  const requestOptions = {
    method: 'GET',
    headers,
  };
  if (debounce) return debounceFetch(url, requestOptions);
  else return fetch(url, requestOptions).then(res => handleResponse(res));
}

function post(url, body, debounce = false, customHeaders = {}) {
  const headers = {
    'X-path': window.location.pathname,
    'Content-Type': 'application/json',
    authorization: `Bearer ${getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE)}`,
    ...workspaceHeader(),
    ...customHeaders,
  };

  const requestOptions = {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  };

  if (debounce) return debounceFetch(url, requestOptions);
  else return fetch(url, requestOptions).then(res => handleResponse(res));
}

function upload(url, method, body, debounce = false) {
  const headers = {
    'X-path': window.location.pathname,
    // 'Content-Type': 'multipart/form-data',
    authorization: `Bearer ${getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE)}`,
    ...workspaceHeader(),
  };

  const requestOptions = {
    method: method === 'POST' ? 'POST' : 'PUT',
    headers,
    // body: JSON.stringify(body),
    body,
  };
  if (debounce) return debounceFetch(url, requestOptions);
  else return fetch(url, requestOptions).then(res => handleResponse(res));
}

function put(url, body, debounce = false) {
  const headers = {
    'X-path': window.location.pathname,
    'Content-Type': 'application/json',
    authorization: `Bearer ${getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE)}`,
    ...workspaceHeader(),
  };
  const requestOptions = {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  };
  if (debounce) return debounceFetch(url, requestOptions);
  else return fetch(url, requestOptions).then(res => handleResponse(res));
}

function _delete(url, body, debounce = false) {
  const headers = {
    'X-path': window.location.pathname,
    'Content-Type': 'application/json',
    authorization: `Bearer ${getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE)}`,
    ...workspaceHeader(),
  };
  const requestOptions = {
    method: 'DELETE',
    headers,
    body: JSON.stringify(body),
  };
  if (debounce) return debounceFetch(url, requestOptions);
  else return fetch(url, requestOptions).then(res => handleResponse(res));
}

function patch(url, body, debounce = false) {
  const headers = {
    'X-path': window.location.pathname,
    'Content-Type': 'application/json',
    authorization: `Bearer ${getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE)}`,
    ...workspaceHeader(),
  };
  const requestOptions = {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  };
  if (debounce) return debounceFetch(url, requestOptions);
  else return fetch(url, requestOptions).then(res => handleResponse(res));
}

export const Fetch = {
  get,
  post,
  put,
  delete: _delete,
  patch,
  upload,
};
